// Catalogue of OffSec services offerable through the New Service Request
// wizard, and the scoping fields rendered per service once selected.
// Service list, descriptions, frameworks and prerequisites are sourced
// from CARAgrc's official "Offensive Security Services — Scoping &
// Prerequisites Input Form" so the Fill Online fields and the downloadable
// Download & Upload templates both reflect the same source of truth.
export const OFFSEC_SERVICES = [
  // Vulnerability Assessment & Penetration Testing
  { key: 'web_app_pentest', label: 'Web Application Penetration Testing' },
  { key: 'mobile_app_pentest', label: 'Mobile Application Penetration Testing' },
  { key: 'api_pentest', label: 'API Penetration Testing' },
  { key: 'external_pentest', label: 'External Penetration Testing' },
  { key: 'internal_network_pentest', label: 'Internal Network Penetration Testing' },
  { key: 'wireless_pentest', label: 'Wireless / Wi-Fi Penetration Testing' },
  { key: 'ai_llm_red_teaming', label: 'AI / LLM Red Teaming' },
  { key: 'secure_source_code_review', label: 'Secure Source Code Review (SAST)' },
  // Security Assessment & Advisory
  { key: 'configuration_review_hardening', label: 'Configuration Review (Hardening Audit)' },
  { key: 'cloud_security_assessment', label: 'Cloud Security Assessment' },
  { key: 'devsecops_pipeline_security', label: 'DevSecOps / CI-CD Pipeline Security' },
  { key: 'managed_vuln_management', label: 'Managed Vulnerability Management' },
];

export const DEFAULT_VISIBLE_SERVICES = 8;

// field types: text | textarea | select | file
export const SCOPING_FIELD_CONFIG = {
  web_app_pentest: {
    title: 'Web Application Penetration Testing',
    fields: [
      { name: 'in_scope_urls', label: 'In-scope URLs & Application Boundaries', type: 'textarea', required: true, placeholder: 'https://app.acme.com\nhttps://admin.acme.com' },
      { name: 'staging_environment', label: 'Staging/UAT Environment Mirroring Production', type: 'select', required: true, options: ['Yes', 'No', 'Not Available'] },
      { name: 'test_accounts', label: 'Test Accounts for Each User Role', type: 'text', required: true, placeholder: 'test.web@acme.com / Welcome123!' },
      { name: 'waf_allowlisting', label: 'WAF/IPS Allow-listing', type: 'text', required: true, placeholder: 'Yes, allow-list required', helpText: "We'll provide tester IPs after submission." },
    ],
  },
  mobile_app_pentest: {
    title: 'Mobile Application Penetration Testing',
    fields: [
      { name: 'app_binaries', label: 'Signed App Builds (APK/IPA) or Store Access', type: 'file', required: true },
      { name: 'test_accounts', label: 'Test Accounts for Each Role', type: 'text', required: true, placeholder: 'test.mobile@acme.com / Welcome123!' },
      { name: 'backend_api_scope', label: 'Backend API Scope & Endpoints', type: 'textarea', required: true, placeholder: 'https://api.acme.com' },
      { name: 'test_device_agreement', label: 'Rooted/Jailbroken Test Device or Emulator Agreement', type: 'select', required: true, options: ['Yes', 'No'] },
    ],
  },
  api_pentest: {
    title: 'API Penetration Testing',
    fields: [
      { name: 'api_documentation', label: 'API Documentation (Swagger/OpenAPI or Postman)', type: 'file', required: true },
      { name: 'endpoint_list', label: 'Full Endpoint & Scope List', type: 'textarea', required: true, placeholder: 'Base URL or list of key endpoints' },
      { name: 'auth_tokens', label: 'Valid Test Credentials & Auth Tokens', type: 'text', required: true, placeholder: 'Bearer eyJhbGciOiJIUzI1NiIsInR5c...', secure: true },
      { name: 'rate_limit_allowlisting', label: 'Rate-limit / Allow-listing for Tester IPs', type: 'text', required: false, placeholder: "We'll provide tester IPs after submission." },
    ],
  },
  external_pentest: {
    title: 'External Penetration Testing',
    fields: [
      { name: 'in_scope_assets', label: 'In-scope Public IPs, Domains & Subdomains', type: 'textarea', required: true, placeholder: 'acme.com, mail.acme.com, 203.0.113.10' },
      { name: 'asset_ownership_proof', label: 'Proof of Asset Ownership (or Third-party/Cloud Approval)', type: 'file', required: true },
      { name: 'escalation_contact', label: 'Escalation Contact', type: 'text', required: true, placeholder: 'Name (email@acme.com)' },
    ],
  },
  internal_network_pentest: {
    title: 'Internal Network Penetration Testing',
    fields: [
      { name: 'ip_ranges', label: 'In-scope IP Ranges / Segments', type: 'textarea', required: true, placeholder: '10.10.0.0/24\n10.20.5.0/28' },
      { name: 'vpn_access', label: 'On-site or VPN Access to the Internal Network', type: 'select', required: true, options: ['Remote VPN access provided', 'On-site tester visit required'] },
      { name: 'assumed_breach_foothold', label: 'Assumed-breach Foothold (Standard User Account / Host)', type: 'text', required: true, placeholder: 'test.internal@acme.com / Welcome123!' },
      { name: 'escalation_contact', label: 'Escalation Contact', type: 'text', required: true, placeholder: 'Name (email@acme.com)' },
    ],
  },
  wireless_pentest: {
    title: 'Wireless / Wi-Fi Penetration Testing',
    fields: [
      { name: 'ssids_in_scope', label: 'In-scope SSIDs', type: 'text', required: true, placeholder: 'Acme-Corp, Acme-Guest' },
      { name: 'site_locations', label: 'Locations Requiring On-site Physical Access', type: 'textarea', required: true, placeholder: 'Office address(es) for on-site testing' },
      { name: 'safe_testing_window', label: 'Safe Testing Window', type: 'text', required: true, placeholder: 'e.g. Weeknights 8 PM – 6 AM local time' },
    ],
  },
  ai_llm_red_teaming: {
    title: 'AI / LLM Red Teaming',
    fields: [
      { name: 'model_endpoint', label: 'AI/LLM Application or API Endpoint (non-production preferred)', type: 'text', required: true, placeholder: 'https://app.acme.com/chat' },
      { name: 'guardrail_documentation', label: 'Model/System & Guardrail Documentation', type: 'file', required: false },
      { name: 'test_accounts', label: 'Test Accounts', type: 'text', required: true, placeholder: 'test.ai@acme.com / Welcome123!' },
      { name: 'policy_scope', label: 'Defined Scope of Policies & Safety Controls', type: 'textarea', required: false, placeholder: 'Optional — helps scope prompt-injection scenarios' },
    ],
  },
  secure_source_code_review: {
    title: 'Secure Source Code Review (SAST)',
    fields: [
      { name: 'repo_access', label: 'Read Access to the Source Code Repository', type: 'text', required: true, placeholder: 'Repo URL or access grant instructions' },
      { name: 'tech_stack_docs', label: 'Language/Tech-stack & Build Documentation', type: 'textarea', required: false, placeholder: 'Languages, frameworks, build process' },
      { name: 'dependency_manifest', label: 'Dependency Manifest (e.g. package files)', type: 'file', required: true },
    ],
  },
  configuration_review_hardening: {
    title: 'Configuration Review (Hardening Audit)',
    fields: [
      { name: 'config_access', label: 'Read-only Access to Devices or Exported Config Files', type: 'text', required: true, placeholder: 'Access method / exported config location' },
      { name: 'asset_inventory', label: 'Asset Inventory — In-scope Systems', type: 'file', required: true },
      { name: 'benchmark_baseline', label: 'Applicable Benchmark Baseline', type: 'select', required: true, options: ['CIS Benchmark', 'DISA STIG', 'NIST SP 800-53'] },
      { name: 'system_contact', label: 'Point of Contact per System', type: 'text', required: true, placeholder: 'Name (email@acme.com)' },
    ],
  },
  cloud_security_assessment: {
    title: 'Cloud Security Assessment',
    fields: [
      { name: 'cloud_provider', label: 'Cloud Provider', type: 'select', required: true, options: ['AWS', 'Azure', 'Google Cloud', 'Multi-cloud'] },
      { name: 'readonly_iam_role', label: 'Read-only Auditor/Security IAM Role per Account', type: 'text', required: true, placeholder: 'arn:aws:iam::111122223333:role/security-audit' },
      { name: 'in_scope_environments', label: 'List of In-scope Environments', type: 'textarea', required: true, placeholder: 'Account / subscription / project IDs' },
      { name: 'architecture_overview', label: 'Cloud Architecture Overview', type: 'file', required: false },
    ],
  },
  devsecops_pipeline_security: {
    title: 'DevSecOps / CI-CD Pipeline Security',
    fields: [
      { name: 'ci_cd_platform', label: 'CI/CD Platform', type: 'select', required: true, options: ['GitHub Actions', 'GitLab CI', 'Jenkins', 'Azure DevOps', 'Other'] },
      { name: 'repo_access_contact', label: 'Read Access to CI/CD Platform & Source Repositories', type: 'text', required: true, placeholder: 'Name (email@acme.com)' },
      { name: 'iac_registry_access', label: 'Access to Registries & Infrastructure-as-Code', type: 'textarea', required: false, placeholder: 'Registry URLs / IaC repo names' },
    ],
  },
  managed_vuln_management: {
    title: 'Managed Vulnerability Management',
    fields: [
      { name: 'asset_inventory', label: 'Asset Inventory for Internal & External Systems', type: 'file', required: true },
      { name: 'scan_frequency', label: 'Scan Authorization & Agreed Windows', type: 'select', required: true, options: ['Weekly', 'Bi-weekly', 'Monthly', 'Quarterly'] },
      { name: 'notification_contact', label: 'Remediation Ownership / Point of Contact', type: 'text', required: true, placeholder: 'Name (email@acme.com)' },
    ],
  },
};

// Content used to generate the downloadable "Download & Upload Form"
// scoping template per service — mirrors the official scoping document
// section-for-section (description, frameworks, prerequisites).
export const SCOPING_TEMPLATE_CONTENT = {
  web_app_pentest: {
    description: 'We approach a web app the way an attacker would, working through authentication, session handling, access control, and the business logic underneath. Testing maps to the OWASP Top 10:2025, covering broken access control, injection, security misconfiguration, and software supply chain issues. The servers behind the app are part of the job too, because a solid application on a weak host still falls. When an app uses AI or LLM features, we add testing from the OWASP Top 10 for LLM Applications, including prompt injection, improper output handling, and sensitive information disclosure.',
    frameworks: 'OWASP Top 10:2025, OWASP Top 10 for LLM Applications, SAST and DAST',
    prerequisites: [
      'In-scope URLs and application boundaries',
      'A staging/UAT environment mirroring production',
      'Test accounts for each user role',
      'WAF/IPS allow-listing for tester IP addresses',
      'Signed authorization and an agreed testing window',
    ],
  },
  mobile_app_pentest: {
    description: 'Mobile work splits between the app on the device and the APIs feeding it. On the device we look at how data is stored, how traffic is protected, how credentials are handled, and whether the binary holds up against tampering and reverse engineering, following the OWASP Mobile Top 10. Most of the real exposure lives in the backend, so the supporting APIs get tested against the OWASP API Security Top 10, including BOLA and BFLA. Apps with AI features get the LLM testing on top.',
    frameworks: 'OWASP Mobile Top 10, OWASP API Security Top 10, iOS and Android',
    prerequisites: [
      'Signed app builds (APK/IPA) or store access',
      'Test accounts for each role',
      'Backend API scope and endpoints',
      'A rooted/jailbroken test device or emulator agreement',
      'Signed authorization',
    ],
  },
  api_pentest: {
    description: 'APIs carry the logic that a user interface never shows, and that is where a lot of the serious bugs hide. We test authorization at the object and function level, where BOLA and BFLA live, along with authentication, token handling, rate limiting, and business logic abuse. Coverage runs across REST, GraphQL, and webhook endpoints, mapped to the OWASP API Security Top 10.',
    frameworks: 'OWASP API Security Top 10, REST and GraphQL, BOLA and BFLA',
    prerequisites: [
      'API documentation (Swagger/OpenAPI or Postman collection)',
      'Full endpoint and scope list',
      'Valid test credentials and auth tokens',
      'Rate-limit/allow-listing for tester IPs',
      'Signed authorization',
    ],
  },
  external_pentest: {
    description: 'This begins where a real intrusion begins, with open-source intelligence on everything you expose to the internet. We map domains, subdomains, and live services, and hunt for leaked credentials and exposed employee data that widen the way in. From there we test the assets that matter, web apps, mail, VPN, and other remote access. The point is to find the opening before someone outside does.',
    frameworks: 'OSINT, PTES, OSSTMM',
    prerequisites: [
      'Confirmed list of in-scope public IPs, domains and subdomains',
      'Proof of asset ownership (and third-party/cloud provider approval where hosted)',
      'Escalation contacts',
      'Signed authorization letter',
    ],
  },
  internal_network_pentest: {
    description: 'We run this as an assumed breach, starting from a position an attacker reaches through a phished user, a planted device, or someone on the inside. From that foothold we chase lateral movement, privilege escalation, and the route to domain admin. Active Directory gets direct attention, including Kerberoasting and credential theft. We also test whether segmentation does what it is supposed to. Method follows PTES, with findings mapped to MITRE ATT&CK.',
    frameworks: 'PTES, MITRE ATT&CK, Active Directory',
    prerequisites: [
      'On-site or VPN access to the internal network',
      'An assumed-breach foothold (standard user account and/or connected host)',
      'In-scope IP ranges and segments',
      'Escalation contact',
      'Signed authorization',
    ],
  },
  wireless_pentest: {
    description: 'A weak wireless setup hands out internal access without anyone touching the perimeter. We test for rogue and evil-twin access points, go after WPA2 and WPA3 authentication, and attempt credential capture and offline cracking. Guest and corporate separation gets checked, along with how far the signal carries beyond the walls you control.',
    frameworks: 'WPA2 and WPA3, Evil Twin, 802.1X',
    prerequisites: [
      'On-site physical access to target facilities',
      'In-scope SSIDs and locations',
      'Safe testing window',
      'Facility access arrangements',
      'Signed authorization',
    ],
  },
  ai_llm_red_teaming: {
    description: 'AI systems fail in their own ways, so they get testing built for them rather than a web checklist. We run jailbreaks and prompt injection, both direct and the indirect kind buried in documents, pages, and emails the model reads. We try to extract training data and sensitive information, push the model past its guardrails, and probe it for biased or unsafe output. For agentic systems we focus on excessive agency, where the model can reach tools or take actions beyond its remit. RAG pipelines get checked for vector and embedding poisoning and system prompt leakage. The work lines up with the OWASP Top 10 for LLM Applications, MITRE ATLAS, and the NIST AI Risk Management Framework.',
    frameworks: 'OWASP Top 10 for LLM Applications, MITRE ATLAS, NIST AI RMF',
    prerequisites: [
      'Access to the AI/LLM application or API (non-production preferred)',
      'Model/system and guardrail documentation',
      'Test accounts',
      'Defined scope of policies and safety controls',
      'Signed authorization',
    ],
  },
  secure_source_code_review: {
    description: 'Some flaws are far easier to spot in the source than from the outside. We pair manual review with static analysis to find injection points, broken logic, weak authentication and crypto, and secrets hardcoded into the codebase. Dependencies get checked against known vulnerabilities. Every finding comes with remediation guidance tied to OWASP and CWE, so the fix is clear to the people writing the code.',
    frameworks: 'SAST, OWASP, CWE',
    prerequisites: [
      'Read access to the source code repository',
      'Language/tech-stack and build documentation',
      'Dependency manifest (e.g. package files)',
      'Signed NDA and authorization',
    ],
  },
  configuration_review_hardening: {
    description: 'A large share of breaches trace back to something left on its default setting. We review configurations against CIS Benchmarks, DISA STIGs, and NIST SP 800-53, across firewalls, routers, switches, servers, endpoints, databases, and cloud. The review pulls out insecure defaults, permissive rules, weak ciphers, and the kind of gaps that surface in a compliance audit.',
    frameworks: 'CIS Benchmarks, DISA STIGs, NIST SP 800-53',
    prerequisites: [
      'Read-only access to devices or exported configuration files',
      'Asset inventory - in-scope systems',
      'Applicable benchmark baseline (CIS/STIG)',
      'A point of contact per system',
    ],
  },
  cloud_security_assessment: {
    description: 'Cloud incidents are usually a configuration problem, not an exotic exploit. We review AWS, Azure, and GCP environments against the CIS cloud benchmarks, with a hard look at IAM and whether least privilege actually holds. Storage exposure, network paths, and key management get checked, along with logging and monitoring baselines. You end up with a clear read on where the environment has drifted from a secure state.',
    frameworks: 'AWS, Azure, GCP, CIS Benchmarks',
    prerequisites: [
      'Read-only auditor/security IAM role for each in-scope account, subscription or project',
      'List of in-scope environments',
      'Cloud architecture overview',
      'A point of contact',
    ],
  },
  devsecops_pipeline_security: {
    description: 'A clean codebase still ships broken when the pipeline around it is weak. We review the CI/CD pipeline for exposed secrets, weak credentials, and the spots where security checks should sit but do not. SAST, DAST, and dependency scanning get wired into the workflow instead of bolted on at the end. Container images, registries, and Infrastructure-as-Code go through configuration review as well.',
    frameworks: 'CI/CD, Containers, Infrastructure-as-Code',
    prerequisites: [
      'Read access to the CI/CD platform and source repositories',
      'Pipeline and build documentation',
      'Access to registries and Infrastructure-as-Code',
      'A point of contact',
    ],
  },
  managed_vuln_management: {
    description: 'A one-off test tells you where you stood that week, and nothing after. This service keeps watch on your assets with recurring scans across internal and external systems. Findings are ranked by real risk using CVSS rather than raw counts, and we track each one through remediation to a retest. You get steady reporting and a clear view of how exposure moves over time.',
    frameworks: 'CVSS, Continuous Scanning, Retesting',
    prerequisites: [
      'Asset inventory for internal and external systems',
      'Scan authorization and agreed windows',
      'Network access or scanner placement plus scan credentials',
      'Defined remediation ownership and a point of contact',
    ],
  },
};
